import config from "@/config";
import pLimit from "p-limit";
import { authentication } from "@/lib/integrations";

class HeaderManager {
    headers: {
        [name: string]: string;
    } = {};
    allHeadersSet: Promise<void>;
    _resolveAllHeadersSet: () => void;
    organizationHeaderSet: Promise<void>;
    _resolveOrganizationHeaderSet: () => void;
    constructor() {
        this.allHeadersSet = new Promise((resolve) => {
            this._resolveAllHeadersSet = resolve;
        });
        this.organizationHeaderSet = new Promise((resolve) => {
            this._resolveOrganizationHeaderSet = resolve;
        });
    }
    updateHeaders(updatedHeaders: { [name: string]: string }) {
        Object.assign(this.headers, updatedHeaders);
        if ("organization" in updatedHeaders) {
            this._resolveOrganizationHeaderSet();
        }
        // Since the headers are set individually, and 'stek' is set after
        // 'organization', assume all headers are set after 'stek' is set.
        if ("stek" in updatedHeaders) {
            this._resolveAllHeadersSet();
        }
    }
}

export const headerManager = new HeaderManager();

const limit = pLimit(config.requests.concurrency);
export async function request(
    url: URL | RequestInfo,
    options: RequestInit = {},
    extraHeaders: Record<string, string> = {}
): Promise<Response> {
    // get extra headers and inject them here :D
    return limit(async () => {
        const token = await authentication.getToken();
        const response = await fetch(url, {
            mode: "cors",
            headers: {
                Authorization: `Bearer ${token}`,
                ...{ ...headerManager.headers, ...extraHeaders },
            },
            ...options,
        });

        if (response.status === 401) {
            await authentication.signOut();
        }

        return response;
    });
}

export interface IRepositoryParameters {
    [variableName: string]: string | number | string[];
}

export interface IFields {
    [name: string]: string | number | IFields | IFields[];
}

type SupportedContentTypes =
    | "application/json"
    | "multipart/form-data"
    | "application/x-www-form-urlencoded";

const ACCEPTED_STATUS_CODE = [200, 201, 204];

function getRequestBody(
    fields: IFields | FormData,
    contentType: SupportedContentTypes
) {
    if (contentType === "application/json") {
        return JSON.stringify(fields);
    } else if (contentType === "application/x-www-form-urlencoded") {
        const formData = new URLSearchParams();
        Object.keys(fields).forEach((key) =>
            formData.append(key, `${fields[key]}`)
        );
        return formData;
    } else if (contentType === "multipart/form-data") {
        const formData = new FormData();
        Object.keys(fields).forEach((key) =>
            formData.append(key, `${fields[key]}`)
        );
        return formData;
    }
}

enum RequestMethod {
    POST = "POST",
    GET = "GET",
    PUT = "PUT",
    DELETE = "DELETE",
    PATCH = "PATCH",
}

const pathToEventEmitter = {};

// Previously we used to listen to events on the repository instances themselves,
// with the removal of those the requestEvents provides a way to listen to related
// events instead.
export const requestEvents = {
    get: (path: string) => {
        if (!pathToEventEmitter[path]) {
            pathToEventEmitter[path] = new EventTarget();
        }
        return pathToEventEmitter[path];
    },
};

// Purely for backwards compatibility.
const methodsToActions = {
    [RequestMethod.POST]: "create",
    [RequestMethod.GET]: "get",
    [RequestMethod.PUT]: "update",
    [RequestMethod.DELETE]: "delete",
    [RequestMethod.PATCH]: "partialUpdate",
};

// Just a convenience method until we make use of a generated client. We
// don't immediately use the generated client because it handles responses
// differently compared to the current native-like implementation.
export function repositoryLikeRequest(
    path: string,
    contentType: SupportedContentTypes = "application/json",
    method: RequestMethod = RequestMethod.POST,
    requiredParameters: string[] = []
) {
    return async (props: IRepositoryParameters, fields: IFields | FormData) => {
        let url = config.bekendURI + path;

        requiredParameters.forEach((parameter) => {
            if (parameter in props) {
                url = url.replace(`{${parameter}}`, `${props[parameter]}`);
            }
        });
        const requestOptions = {};
        // Avoid setting the content type ourselves in case of formdata, so that
        // the browser can configure it and set the correct boundaries.
        if (contentType !== "multipart/form-data") {
            requestOptions["Content-Type"] = contentType;
        }

        const response = await request(
            url,
            {
                method,
                body: getRequestBody(fields, contentType),
            },
            requestOptions
        );

        if (!ACCEPTED_STATUS_CODE.includes(response.status)) {
            return response;
        }

        const action = methodsToActions[method];
        let eventName = action + "d";
        if (action === "partialUpdate") {
            eventName = "patched";
        }

        requestEvents.get(path).dispatchEvent(new CustomEvent(eventName));
        requestEvents
            .get(path)
            .dispatchEvent(new CustomEvent(`repository:${eventName}`));
        return response;
    };
}
